import type { CAREER_ID } from "enefel/career";
import { getCareerFromKey } from "enefel/race";
import { isSpecialSkill, SKILL_NAMES } from "enefel/skill";
import type { PlayerSkill } from "enefel/types/models";
import React from "react";
import Skill from "./Skill";

// Définition des props avec typage strict
export type SpecialSkillsProps = {
  playerId?: number | string | null;
  careerId?: CAREER_ID | null;
  skills?: (PlayerSkill | string | [string, number])[];
  separator?: React.ReactNode;
  empty?: React.ReactNode;
  hide?: string[];
};

const SpecialSkills: React.FC<SpecialSkillsProps> = React.memo(
  ({
    playerId = null,
    careerId = null,
    skills = [],
    separator = <br />,
    empty = null,
    hide = [],
  }) => {
    // Les skills de la carrière sont des SKILL_NAMES ou [SKILL_NAMES, number]
    let careerSkills: (string | [string, number])[] = [];
    if (careerId) {
      const career = getCareerFromKey(careerId);
      careerSkills = career ? career.skills : [];
    }

    // Si skills est vide mais careerId est fourni, utiliser les compétences de la carrière
    const skillsToUse =
      skills.length > 0 ? skills : careerId ? careerSkills : [];

    // Normalisation des entrées (player skills vs career definitions)
    const normalized = skillsToUse.map((s) => {
      if (typeof s === "string") {
        return { skill_id: s, info: undefined } as any;
      }
      if (Array.isArray(s)) {
        return { skill_id: s[0], info: s[1] } as any;
      }
      return s as PlayerSkill;
    });

    const animosities = normalized.filter(
      (s) => s.skill_id === SKILL_NAMES.ANIMOSITY
    );

    const list = normalized
      .filter((s) => {
        const isSpecial = isSpecialSkill(s.skill_id as any);
        const notHidden = !hide.includes(s.skill_id);
        const notAnimosity = s.skill_id !== SKILL_NAMES.ANIMOSITY;

        return isSpecial && notHidden && notAnimosity;
      })
      .map((s) => {
        const key = `${s.skill_id}-${playerId}-${s.info}`;
        const isNew =
          careerId && careerSkills.length
            ? !careerSkills.some(
                (r) =>
                  (typeof r === "string" && r === s.skill_id) ||
                  (Array.isArray(r) && r[0] === s.skill_id)
              )
            : false;

        return (
          <Skill
            key={key}
            skill={s}
            isNew={isNew}
            info={s.info}
            withDate={true}
          />
        );
      });

    if (animosities.length > 0) {
      list.push(
        <Skill
          key="animosity-skill"
          skill={SKILL_NAMES.ANIMOSITY}
          info={animosities.map((a) => a.info)}
          withDate={true}
        />
      );
    }

    return list && list.length ? (
      <>
        {list.map((el, i) => (
          <React.Fragment key={el.key || i}>
            {i > 0 && separator}
            {el}
          </React.Fragment>
        ))}
      </>
    ) : empty ? (
      <>{empty}</>
    ) : null;
  }
);

export default SpecialSkills;
